import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import Message4Line from '@/assets/icons/message_4_line.svg?component';
import { Button } from '@/components/atoms/Buttons';
import Select from '@/components/atoms/Select';
import { SvgIcon } from '@/components/atoms/SvgIcon/SvgIcon';
import DropDownPanel from '@/components/molecules/DropDownPanel';
import type { ImageFeedback } from '@/domain/imageFeedback';
import { type DetectionOption, dropdownDetectionOptions } from '@/utils/falseDetectionOptions';

import styles from './ShareFalseDetection.module.scss';

const FEEDBACK_BUTTON_NAME = 'feedback';

export interface ShareFalseDetectionProps {
    /** Starts with the report form open. */
    open?: boolean;
    isOptoScaleAdmin?: boolean;
    onCommitFalseDetection: (feedbackDto: ImageFeedback) => void;
    showLiceOptions: boolean;
    showHealthOptions: boolean;
    showLaksvelOptions?: boolean;
}

export default function ShareFalseDetection(props: ShareFalseDetectionProps) {
    const {
        isOptoScaleAdmin = false,
        onCommitFalseDetection = () => {},
        showLiceOptions = false,
        showHealthOptions = false,
        showLaksvelOptions = false,
        open = false,
    } = props;

    const { t } = useTranslation();

    const [isOpen, setIsOpen] = useState<boolean>(open);
    const [selectedOptionValue, setSelectedOptionValue] = useState('');

    const filteredOptions: DetectionOption[] = dropdownDetectionOptions.filter((dropdownOption) => {
        if (dropdownOption.onlyOptoScaleAdmin) return isOptoScaleAdmin;
        if (dropdownOption.type === 'lice') return showLiceOptions;
        if (dropdownOption.type === 'health') return showHealthOptions;
        if (dropdownOption.type === 'laksvel') return showLaksvelOptions;
        if (dropdownOption.type === 'external') return true;
        return false;
    });

    // Closing however it happens (Cancel, Send, Escape, a click outside) drops the choice,
    // so the form opens empty next time.
    const handleOpenChange = (next: boolean) => {
        setIsOpen(next);
        if (!next) setSelectedOptionValue('');
    };

    const commitFalseDetection = () => {
        const feedbackDto: ImageFeedback = {
            imageMetadataDto: undefined,
            tag: selectedOptionValue,
        };
        onCommitFalseDetection(feedbackDto);
        handleOpenChange(false);
    };

    if (filteredOptions.length === 0) {
        return null;
    }

    return (
        <DropDownPanel
            label={t('organisms.imageViewer.wrongDetection')}
            tooltip={t('organisms.imageViewer.sendAReportAboutAn')}
            variant="toolbar"
            trackingId="image_viewer_false_detection_open"
            name={FEEDBACK_BUTTON_NAME}
            icon={<SvgIcon color="currentColor" icon={Message4Line} />}
            isOpen={isOpen}
            onOpenChange={handleOpenChange}>
            <fieldset className={styles.content}>
                <legend>{t('organisms.imageViewer.submitAReportForThe')}</legend>
                <Select
                    aria-label={t('organisms.imageViewer.selectTypeOfDetection')}
                    value={selectedOptionValue}
                    className={styles.reasonSelect}
                    onChange={(event) => setSelectedOptionValue(event.target.value)}>
                    <option value="" disabled>
                        {t('organisms.imageViewer.selectTypeOfDetection')}
                    </option>
                    {filteredOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                            {t(option.label)}
                        </option>
                    ))}
                </Select>
                <div className={styles.buttonContainer}>
                    <Button
                        variant="secondary"
                        size="medium"
                        trackingId="image_viewer_false_detection_cancel"
                        onClick={() => handleOpenChange(false)}>
                        {t('common.actions.cancel')}
                    </Button>
                    <Button
                        disabled={selectedOptionValue === ''}
                        variant="primary"
                        size="medium"
                        trackingId="image_viewer_false_detection_send"
                        onClick={commitFalseDetection}>
                        {t('organisms.imageViewer.sendReport')}
                    </Button>
                </div>
            </fieldset>
        </DropDownPanel>
    );
}
